import React from 'react';
import { Award, Heart, BookOpen, Users } from 'lucide-react';
import { Card } from './ui/card';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Readiness-Based Learning',
      description: 'Children are guided according to their individual readiness, pace, and confidence, so learning feels meaningful and natural.'
    },
    {
      icon: BookOpen,
      title: 'Structured Approach',
      description: 'Clear, step-by-step methods that build confidence and competence.'
    },
    {
      icon: Users,
      title: 'Empowering Adults',
      description: 'Supporting parents and teachers with knowledge and practical skills.'
    },
    {
      icon: Award,
      title: '20+ Years Experience',
      description: 'Proven expertise in early childhood education and literacy development.'
    }
  ];

  return (
    <section id="about" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            About Us
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Meet Teacher Kalah
          </h2>
          <p className="text-base sm:text-lg text-[#3B4BA0] font-semibold mb-3 leading-snug">
            Early Childhood Educator&nbsp;·&nbsp;Montessori Trainer&nbsp;·&nbsp;Brain Gym Practitioner&nbsp;·&nbsp;Phonics &amp; Reading Specialist
          </p>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Over 20 years of experience in early years education, school
            leadership, phonics instruction and teacher training.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left - Photo */}
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl shadow-xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/hgf3789w_ChatGPT%20Image%20Jun%2019%2C%202026%2C%2009_02_44%20PM.png"
                alt="Kalah K. Singam - Founder of Read Right Academy"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative Element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-teal-400/20 rounded-full blur-2xl -z-10" />
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-400/20 rounded-full blur-2xl -z-10" />
          </div>

          {/* Right - Content */}
          <div className="space-y-6">
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong className="text-[#3B4BA0]">K. Kalah Kulasingam</strong>,
              fondly known as <strong>Teacher Kalah</strong>, is an Early
              Childhood Educator, Montessori Trainer, Brain Gym Practitioner
              and Phonics &amp; Reading Specialist with over 20 years of
              experience in teaching, curriculum design, school leadership and
              teacher training.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              She holds a Master&apos;s in Early Childhood Education from Open
              University Malaysia, a HEI Schools Teaching Diploma from the
              University of Helsinki, a Teacher Trainer Certificate from
              Montessori Centre International UK, an International Teacher
              Certificate from ECIS / University of Cambridge, an
              International Diploma in Early Childhood Education from
              Montessori Centre International London, and Brain Gym training.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Through Read Right Academy, Teacher Kalah supports young
              children in building strong foundations in phonics, reading,
              vocabulary, comprehension and confidence. Her work also includes
              mentoring teachers, guiding parents and developing structured
              early literacy programmes that support children&apos;s language,
              movement, focus and early learning readiness.
            </p>
            <div className="bg-blue-50 border-l-4 border-[#3B4BA0] p-6 rounded-r-lg">
              <p className="text-lg font-semibold text-gray-900 italic">
                &ldquo;Reading grows best when a child is prepared with
                patience, guided with clarity, and encouraged with joy.&rdquo;
              </p>
              <p className="text-sm text-gray-600 mt-2">— Teacher Kalah</p>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => {
            const IconComponent = value.icon;
            return (
              <Card key={index} className="p-6 border-2 hover:border-[#3B4BA0] transition-all duration-300 hover:shadow-lg group">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#3B4BA0] transition-colors duration-300">
                  <IconComponent className="h-6 w-6 text-[#3B4BA0] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
              </Card>
            );
          })}
        </div>

        {/* Academy Description */}
        <div className="mt-20 text-center max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-gray-900 mb-6">About Read Right Academy</h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Read Right Academy is an early literacy and teacher-training platform dedicated to 
            helping children become confident readers and learners.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            We provide structured phonics, early reading, writing readiness, vocabulary, 
            comprehension, and kindergarten readiness programmes for young children.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            The academy also supports parents, teachers, and aspiring educators through practical 
            training, workshops, and mentoring. At Read Right Academy, learning is not rushed. 
            Children are guided step by step with patience and care, building not only reading 
            skills but also confidence, understanding, and a love for learning.
          </p>
        </div>
      </div>
    </section>
  );
};

export default About;